import { useTranslation } from 'react-i18next';
import './AvailableCertifications.scss';
import Subtitle from 'components/TextComps/Subtitle';
import Paragraph from 'components/TextComps/Paragraph';
import { useCartStore } from 'stores/Webshop/CartStore';
import { ShopCertificate } from 'types/pricing';
import { useStores } from 'index';
import defaultCertificateBadge from 'images/Certification/DefaultCertificateBadge.svg';
import { ReactComponent as Plus } from 'images/icons/Plus.svg';
import { IconButton, Loader } from '@trackman/web-shared-components';
import { CartItem } from 'types/cart';
import { addCartItemToLocalStorage } from 'utilities/cartLocalStorageHelper';
import { AddedToCart } from 'pages/Webshop/AddedToCart/AddedToCart';
import { useIsUserMember } from 'queryHooks/permissions';
import { useGetShopCertifications } from 'queryHooks/certificates';

export const AvailableCertifications = () => {
  const { t } = useTranslation();
  const notificationsStore = useStores().notificationStore;

  const { data: isPremium, isPending: isPremiumPending } = useIsUserMember();
  const { data: certificates, isPending: certificatesPending } = useGetShopCertifications();
  const itemsInCart = useCartStore((state) => state.items);

  const addToCart = useCartStore((state) => state.addItemToCart);

  const availableCertificates = certificates?.filter((cert) => cert.status === 'Unlocked');
  const hasDiscount = (!isPremiumPending && isPremium) || itemsInCart.some((item) => item.id === 'premium');
  const getAreAllCertificatesInCart = availableCertificates?.every((certificate) => itemsInCart.some((item) => item.id === certificate.id));

  const getIsItemInCart = (certificateId: string) => {
    return itemsInCart.some((item) => item.id === certificateId);
  };

  const handleAddToCart = (certificate: CartItem) => {
    addToCart(certificate);
    addCartItemToLocalStorage(certificate);
    notificationsStore.addToast(
      t('AddedToCart', { ns: 'webshop' }),
      t('CertificateHasBeenAddedToCart', { ns: 'webshop', certificateName: certificate.title }),
      'success'
    );
  };

  return (
    <>
      {!certificatesPending ? (
        <div className='available-certifications'>
          {getAreAllCertificatesInCart ? (
            <></>
          ) : (
            <div className='available-certifications-header'>
              <Subtitle>
                <>{t('WouldYouLikeToAddSomethingElse', { ns: 'webshop' })}</>
              </Subtitle>
              <Paragraph>{t('AddAnotherCertification', { ns: 'webshop' })}</Paragraph>
            </div>
          )}

          {
            <div className='certifications-list'>
              {availableCertificates?.map((certificate, index) => (
                <CertificationCard
                  key={index}
                  certificate={certificate}
                  isItemInCart={getIsItemInCart(certificate.id)}
                  hasDiscount={hasDiscount}
                  addToCart={(certificate: CartItem) => handleAddToCart(certificate)}
                />
              ))}
            </div>
          }
        </div>
      ) : (
        <div className='available-certifications-loader'>
          <Loader size='large' />
        </div>
      )}
    </>
  );
};

const CertificationCard = ({
  certificate,
  hasDiscount,
  isItemInCart,
  addToCart,
}: {
  certificate: ShopCertificate;
  hasDiscount: boolean;
  isItemInCart: boolean;
  addToCart: (certificate: CartItem) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className='certification-card'>
      <div className='certification-card-header'>
        <img
          src={certificate.badgeImageUrl ? certificate.badgeImageUrl : defaultCertificateBadge}
          alt={certificate.title ?? ''}
        />
        {!isItemInCart ? (
          <IconButton
            icon={() => <Plus />}
            intent='secondary'
            onClick={() =>
              addToCart({
                id: certificate.id,
                title: certificate.title,
                price: certificate.price,
              })
            }
          />
        ) : (
          <AddedToCart />
        )}
      </div>
      <div className='certification-card-body'>
        <Subtitle>
          <>{certificate.title}</>
        </Subtitle>
        <Paragraph>
          <>{certificate.description}</>
        </Paragraph>
        <div>
          <span>{hasDiscount ? `$${certificate.price * 0.5}` : `$${certificate.price}`}</span>
          <span className={hasDiscount ? 'base-price' : 'get-discount'}>
            {hasDiscount ? `$${certificate.price}` : t('Get50%Off', { ns: 'webshop' })}
          </span>
        </div>
      </div>
    </div>
  );
};
